<!--
  BrowseFilterBar — the one filter bar above every presentation of the search (Spec 120 E3, E4).
  Wraps FilterToolbar with the search filters and adds the "Zeitraum" dropdown chip (Spec 126). Owns no state:
  the parent passes the current filters and applies `filter` / `clearAll` events.
-->
<script lang="ts" context="module">
  export interface BrowseFilters {
    search: string | null;
    category: string | null;
    subCategory: string | null;
    reviewStatus: 'unreviewed' | 'reviewed' | null;
    fromDate: string | null;
    toDate: string | null;
    assignedTo: string | null;
    userTag: string | null;
    fileFormat: string | null;
    minSize: number | null;
    maxSize: number | null;
    householdId: string | null;
  }
</script>

<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  import Check from 'lucide-svelte/icons/check';
  import ChevronDown from 'lucide-svelte/icons/chevron-down';
  import X from 'lucide-svelte/icons/x';
  import { _ } from 'svelte-i18n';
  import type { Category, UserTagCount } from '../api';
  import { FilterToolbar } from './ui/index';
  import DateInput from './ui/DateInput.svelte';
  import type { FilterDef, FilterValue } from './ui/FilterToolbar.svelte';
  import { userSettings } from '../stores/settingsStore';
  import { formatDate, type UserSettings } from '../settings';
  import { TIME_RANGE_KEYS, timeRangeFor, type TimeRangeKey } from '../browseTimeRanges';

  export let filters: BrowseFilters;
  export let categories: Category[] = [];
  export let userTags: UserTagCount[] = [];
  export let members: Array<{ id: string; name: string }> = [];
  export let households: Array<{ id: string; name?: string }> = [];

  const dispatch = createEventDispatcher<{
    filter: { key: string; value: FilterValue };
    clearAll: void;
  }>();

  const FORMAT_OPTIONS = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const FORMAT_LABELS = ['PDF', 'JPEG', 'PNG', 'Word (DOC)', 'Word (DOCX)'];
  const TIME_LABEL_KEYS: Record<TimeRangeKey, string> = {
    last30Days: 'pages.browse.time_last30_days',
    quarter: 'pages.browse.time_quarter',
    halfYear: 'pages.browse.time_half_year',
    thisYear: 'pages.browse.time_this_year',
    lastYear: 'pages.browse.time_last_year',
  };

  $: selectedCategory = categories.find((c) => c.name === filters.category) ?? null;
  // The active assignee stays offered even if they are not (or no longer) a household member.
  $: assigneeIds = [
    ...members.map((m) => m.id),
    ...(filters.assignedTo && !members.some((m) => m.id === filters.assignedTo)
      ? [filters.assignedTo]
      : []),
  ];

  $: availableFilters = [
    { key: 'search', label: $_('table.filter.search'), type: 'text' as const },
    {
      key: 'reviewStatus',
      label: $_('pages.browse.review_status'),
      type: 'enum' as const,
      options: ['unreviewed', 'reviewed'],
      optionLabels: [$_('pages.browse.review_unreviewed'), $_('pages.browse.review_reviewed')],
    },
    {
      key: 'category',
      label: $_('components.document_table.column_category'),
      type: 'enum' as const,
      options: categories.map((c) => c.name),
      optionLabels: categories.map((c) => c.displayName),
    },
    {
      key: 'subCategory',
      label: $_('components.document_table.column_subcategory'),
      type: 'enum' as const,
      options: selectedCategory?.subCategories.map((s) => s.name) ?? [],
      optionLabels: selectedCategory?.subCategories.map((s) => s.displayName) ?? [],
    },
    {
      key: 'assignedTo',
      label: $_('components.document_table.column_assigned_to'),
      type: 'enum' as const,
      options: assigneeIds,
      optionLabels: assigneeIds.map((id) => members.find((m) => m.id === id)?.name ?? id),
    },
    ...(userTags.length > 0
      ? [
          {
            key: 'userTag',
            label: $_('components.document_table.column_user_tag'),
            type: 'enum' as const,
            options: userTags.map((t) => t.tag),
            optionLabels: userTags.map((t) => t.tag),
            optionCounts: userTags.map((t) => t.count),
          },
        ]
      : []),
    {
      key: 'fileFormat',
      label: $_('components.document_table.column_format'),
      type: 'enum' as const,
      options: FORMAT_OPTIONS,
      optionLabels: FORMAT_LABELS,
    },
    {
      key: 'fileSize',
      label: $_('components.document_table.column_size'),
      type: 'number-range' as const,
    },
    {
      key: 'household',
      label: $_('pages.browse.household_scope'),
      type: 'enum' as const,
      options: households.map((h) => h.id),
      optionLabels: households.map((h) => h.name || h.id),
    },
  ] satisfies FilterDef[];

  $: activeFilters = toolbarFilters(filters);

  function toolbarFilters(f: BrowseFilters): Record<string, FilterValue> {
    const result: Record<string, FilterValue> = {};
    if (f.search) result.search = f.search;
    if (f.reviewStatus) result.reviewStatus = [f.reviewStatus];
    if (f.category) result.category = [f.category];
    if (f.subCategory) result.subCategory = [f.subCategory];
    if (f.assignedTo) result.assignedTo = [f.assignedTo];
    if (f.userTag) result.userTag = [f.userTag];
    if (f.fileFormat) result.fileFormat = [f.fileFormat];
    if (f.minSize != null || f.maxSize != null)
      result.fileSize = { min: f.minSize, max: f.maxSize };
    if (f.householdId) result.household = [f.householdId];
    // Counts for the badge and "clear all"; its chip is the dropdown below.
    if (f.fromDate || f.toDate) result.dateRange = { from: f.fromDate, to: f.toDate };
    return result;
  }

  // The time range whose dates equal the active ones is marked; any other pair is "free dates".
  $: activeTimeKey = matchingTimeKey(filters.fromDate, filters.toDate);
  $: hasRange = !!(filters.fromDate || filters.toDate);
  $: rangeLabel = hasRange
    ? $_('pages.browse.time_range_active', {
        values: {
          range: describeRange(filters.fromDate, filters.toDate, activeTimeKey, $userSettings),
        },
      })
    : $_('pages.browse.time_range_label');

  function matchingTimeKey(fromDate: string | null, toDate: string | null): TimeRangeKey | null {
    const today = new Date();
    return (
      TIME_RANGE_KEYS.find((key) => {
        const range = timeRangeFor(key, today);
        return range.fromDate === fromDate && range.toDate === toDate;
      }) ?? null
    );
  }

  function describeRange(
    from: string | null,
    to: string | null,
    key: TimeRangeKey | null,
    settings: UserSettings,
  ) {
    if (key) return $_(TIME_LABEL_KEYS[key]);
    if (from && to) return `${formatDate(from, settings)} – ${formatDate(to, settings)}`;
    if (from)
      return $_('pages.browse.time_range_since', { values: { date: formatDate(from, settings) } });
    return $_('pages.browse.time_range_until', { values: { date: formatDate(to!, settings) } });
  }

  let menuOpen = false;
  let chipEl: HTMLButtonElement;
  let menuEl: HTMLDivElement | undefined;
  let wrapEl: HTMLDivElement | undefined;

  const menuOptions = () =>
    Array.from(menuEl?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []);

  async function toggleMenu() {
    menuOpen = !menuOpen;
    if (!menuOpen) return;
    await tick();
    const options = menuOptions();
    (options.find((o) => o.getAttribute('aria-checked') === 'true') ?? options[0])?.focus();
  }

  function closeMenu(returnFocus: boolean) {
    menuOpen = false;
    if (returnFocus) chipEl?.focus();
  }

  function onDropdownKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && menuOpen) {
      e.stopPropagation();
      closeMenu(true);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const options = menuOptions();
    const at = options.indexOf(e.target as HTMLElement);
    if (at < 0) return;
    e.preventDefault();
    const step = e.key === 'ArrowDown' ? 1 : -1;
    options[(at + step + options.length) % options.length].focus();
  }

  // Capture phase: "+ Filter" stops propagation of its click, a bubbling listener would never see it.
  function onWindowClick(e: MouseEvent) {
    if (menuOpen && !(e.target as HTMLElement).closest('.browse-time-dropdown')) closeMenu(false);
  }

  // A null relatedTarget (click on padding, window blur) is not "left the menu"; outside clicks
  // are handled by onWindowClick.
  function onFocusOut(e: FocusEvent) {
    const next = e.relatedTarget as Node | null;
    if (menuOpen && next && !wrapEl?.contains(next)) closeMenu(false);
  }

  function clearRange() {
    emitRange(null, null);
    // The cross disappears with the selection; keep the focus on the chip.
    chipEl?.focus();
  }

  function emitRange(from: string | null, to: string | null) {
    dispatch('filter', { key: 'dateRange', value: from || to ? { from, to } : null });
  }

  function pickTimeRange(key: TimeRangeKey) {
    const { fromDate, toDate } = timeRangeFor(key, new Date());
    emitRange(fromDate, toDate);
    closeMenu(true);
  }

  const chipBase =
    'px-2.5 py-1 text-xs font-medium border transition-colors cursor-pointer inline-flex items-center gap-1';
  // Same look as the active chips of FilterToolbar.
  const chipOn = 'border-accent/20 bg-accent/10 text-accent hover:bg-accent/20';
  const chipOff =
    'border-border bg-transparent text-text-muted hover:text-text hover:bg-surface-hover';
</script>

<svelte:window on:click|capture={onWindowClick} />

<FilterToolbar
  barTestId="browse-filterbar"
  addTestId="browse-filter-add"
  {availableFilters}
  {activeFilters}
  persistentKeys={['household']}
  hiddenChipKeys={['dateRange']}
  on:filterChange={(e) => dispatch('filter', e.detail)}
  on:filterRemove={(e) => dispatch('filter', { key: e.detail.key, value: null })}
  on:clearAll={() => dispatch('clearAll')}
>
  <svelte:fragment slot="after-chips">
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="relative browse-time-dropdown"
      bind:this={wrapEl}
      on:keydown={onDropdownKeydown}
      on:focusout={onFocusOut}
    >
      <div class="inline-flex items-stretch" data-testid="browse-time-group">
        <button
          type="button"
          bind:this={chipEl}
          data-testid="browse-time-chip"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          class="{chipBase} {hasRange
            ? `${chipOn} rounded-l-full border-r-0`
            : `${chipOff} rounded-full`}"
          on:click={toggleMenu}
        >
          {rangeLabel}
          <ChevronDown class="w-3 h-3" strokeWidth={2} />
        </button>
        {#if hasRange}
          <button
            type="button"
            data-testid="browse-time-clear"
            aria-label={$_('pages.browse.time_range_clear')}
            class="{chipBase} {chipOn} rounded-r-full border-l-0 pl-1 items-center"
            on:click={clearRange}
          >
            <X class="w-3 h-3" strokeWidth={2} />
          </button>
        {/if}
      </div>
      {#if menuOpen}
        <!-- Below sm the panel is anchored to the viewport so it cannot overflow the screen. -->
        <div
          bind:this={menuEl}
          data-testid="browse-time-menu"
          data-esc-owner
          class="z-20 rounded-md border border-border bg-surface p-1 shadow-lg max-sm:fixed max-sm:inset-x-4 max-sm:mt-1 sm:absolute sm:left-0 sm:top-full sm:mt-1 sm:w-56"
        >
          <div role="menu" aria-label={$_('pages.browse.time_range_menu')}>
            {#each TIME_RANGE_KEYS as key (key)}
              <button
                type="button"
                role="menuitemradio"
                aria-checked={activeTimeKey === key}
                data-testid="browse-time-option-{key}"
                class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-text hover:bg-surface-hover cursor-pointer border-none bg-transparent"
                on:click={() => pickTimeRange(key)}
              >
                <span class="w-4">
                  {#if activeTimeKey === key}<Check class="w-3.5 h-3.5" strokeWidth={2} />{/if}
                </span>
                {$_(TIME_LABEL_KEYS[key])}
              </button>
            {/each}
          </div>
          <div
            role="group"
            aria-label={$_('pages.browse.time_range_free')}
            class="mt-1 flex flex-col gap-2 border-t border-border px-2 pt-2 pb-1"
          >
            <label class="flex flex-col gap-1 text-xs text-text-muted">
              {$_('pages.browse.time_range_from')}
              <DateInput
                value={filters.fromDate ?? ''}
                on:change={(e) => emitRange(e.detail || null, filters.toDate)}
                data-testid="browse-time-from"
              />
            </label>
            <label class="flex flex-col gap-1 text-xs text-text-muted">
              {$_('pages.browse.time_range_to')}
              <DateInput
                value={filters.toDate ?? ''}
                on:change={(e) => emitRange(filters.fromDate, e.detail || null)}
                data-testid="browse-time-to"
              />
            </label>
          </div>
        </div>
      {/if}
    </div>
  </svelte:fragment>
</FilterToolbar>
